import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Ltr, Row, Spinner } from './ui.js';

interface AudienceUser {
  telegramUserId: string;
  telegramId: string;
  username: string | null;
  displayName: string | null;
  active: boolean;
  blockedBot: boolean;
  member: boolean;
  botRole: string | null;
  linked: boolean;
  jiraName: string | null;
  teams: string[];
  startedAt: string;
  lastSeenAt: string | null;
}

interface Broadcast {
  id: string;
  text: string;
  audience: string;
  audienceNote: string | null;
  status: string;
  total: number;
  sent: number;
  failed: number;
  lastError: string | null;
  createdAt: string;
  author: string | null;
}

type Audience = 'ALL' | 'SELECTED' | 'TEAM';

export function BroadcastTab(): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();

  const [text, setText] = useState('');
  const [audience, setAudience] = useState<Audience>('SELECTED');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);

  const people = useQuery({
    queryKey: ['broadcast-audience', search],
    queryFn: () =>
      api<{ total: number; users: AudienceUser[] }>(
        `/admin/broadcast/audience?take=500${search ? `&q=${encodeURIComponent(search)}` : ''}`,
      ),
  });

  const teams = useQuery({
    queryKey: ['admin-teams'],
    queryFn: () => api<{ teams: Array<{ id: string; name: string; members: unknown[] }> }>('/admin/teams'),
  });

  const history = useQuery({
    queryKey: ['broadcast-history'],
    queryFn: () => api<{ broadcasts: Broadcast[] }>('/admin/broadcast'),
    refetchInterval: 10_000,
  });

  const send = useMutation({
    mutationFn: () =>
      api<{ id: string; total: number }>('/admin/broadcast', {
        method: 'POST',
        body: {
          text,
          audience,
          telegramUserIds: audience === 'SELECTED' ? selected : undefined,
          teamIds: audience === 'TEAM' ? teamIds : undefined,
        },
      }),
    onSuccess: () => {
      setText('');
      setSelected([]);
      void client.invalidateQueries({ queryKey: ['broadcast-history'] });
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api(`/admin/broadcast/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['broadcast-history'] }),
  });

  const rows = people.data?.users ?? [];
  const toggle = (id: string): void =>
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const recipientCount =
    audience === 'ALL'
      ? (people.data?.total ?? 0)
      : audience === 'TEAM'
        ? (teams.data?.teams ?? [])
            .filter((team) => teamIds.includes(team.id))
            .reduce((sum, team) => sum + team.members.length, 0)
        : selected.length;

  return (
    <>
      <Card title={t('admin.broadcast')}>
        <div className="note small">{t('admin.broadcast_hint')}</div>

        <div className="field">
          <label htmlFor="bc-text">{t('admin.broadcast_text')}</label>
          <textarea
            id="bc-text"
            rows={5}
            value={text}
            maxLength={3800}
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="bc-audience">{t('admin.broadcast_audience')}</label>
          <select
            id="bc-audience"
            value={audience}
            onChange={(event) => setAudience(event.target.value as Audience)}
          >
            <option value="SELECTED">{t('admin.broadcast_audience_selected')}</option>
            <option value="TEAM">{t('admin.broadcast_audience_team')}</option>
            <option value="ALL">{t('admin.broadcast_audience_all')}</option>
          </select>
        </div>

        {audience === 'TEAM' ? (
          <div className="scroll-x">
            {(teams.data?.teams ?? []).map((team) => (
              <button
                key={team.id}
                type="button"
                className={`chip ${teamIds.includes(team.id) ? 'chip-done' : ''}`}
                onClick={() =>
                  setTeamIds((current) =>
                    current.includes(team.id)
                      ? current.filter((item) => item !== team.id)
                      : [...current, team.id],
                  )
                }
              >
                {team.name} ({team.members.length})
              </button>
            ))}
          </div>
        ) : null}

        <div className="btn-row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={text.trim().length === 0 || recipientCount === 0 || send.isPending}
            onClick={() => send.mutate()}
          >
            {t('admin.broadcast_send')} · {recipientCount}
          </button>
        </div>
        {send.error ? <ErrorBox error={send.error} /> : null}
        {send.data ? (
          <div className="note small">{t('admin.broadcast_queued', { count: send.data.total })}</div>
        ) : null}
      </Card>

      {audience === 'SELECTED' ? (
        <Card title={t('admin.audience_total', { count: people.data?.total ?? 0 })}>
          <div className="field">
            <input
              value={search}
              placeholder={t('admin.audience_search')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSelected(rows.map((user) => user.telegramUserId))}
            >
              {t('admin.broadcast_select_all')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setSelected([])}>
              {t('admin.broadcast_clear')}
            </button>
            <span className="chip">{t('admin.broadcast_selected', { count: selected.length })}</span>
          </div>

          {people.isLoading ? <Spinner /> : null}
          {people.error ? <ErrorBox error={people.error} /> : null}
          {!people.isLoading && rows.length === 0 ? <Empty /> : null}

          {rows.map((user) => (
            <div
              key={user.telegramUserId}
              className="row"
              style={{ display: 'block', cursor: 'pointer' }}
              onClick={() => toggle(user.telegramUserId)}
            >
              <div className="row-title">
                {selected.includes(user.telegramUserId) ? '☑ ' : '☐ '}
                {user.displayName ?? user.username ?? <Ltr>{user.telegramId}</Ltr>}
              </div>
              <div className="row-sub">
                <span className={`chip ${user.linked ? 'chip-done' : ''}`}>
                  {user.linked ? t('admin.audience_linked') : t('admin.audience_not_linked')}
                </span>{' '}
                {user.botRole ? <span className="chip">{user.botRole}</span> : null}{' '}
                {user.teams.map((team) => (
                  <span key={team} className="chip">
                    {team}
                  </span>
                ))}{' '}
                <span className="muted small">
                  {t('admin.audience_started', {
                    when: new Date(user.startedAt).toLocaleDateString(),
                  })}
                </span>
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      <Card title={t('admin.broadcast_history')}>
        {(history.data?.broadcasts ?? []).length === 0 ? <Empty /> : null}
        {(history.data?.broadcasts ?? []).map((row) => (
          <Row
            key={row.id}
            title={row.text.slice(0, 80)}
            subtitle={`${t('admin.broadcast_progress', {
              sent: row.sent,
              failed: row.failed,
              total: row.total,
            })} · ${new Date(row.createdAt).toLocaleString()}`}
            right={
              row.status === 'RUNNING' || row.status === 'QUEUED' ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => cancel.mutate(row.id)}
                >
                  {t('admin.broadcast_cancel')}
                </button>
              ) : (
                <span className={`chip ${row.status === 'DONE' ? 'chip-done' : 'chip-danger'}`}>
                  {row.status}
                </span>
              )
            }
          />
        ))}
      </Card>
    </>
  );
}
